/* V190 — Anti-RankPill Content Theft Defense Admin UI
 * Lists detected theft incidents, allows admin to mark them resolved /
 * DMCA-filed / ignored. Top status card shows canary count and CSE config.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_FILTERS = [
  { key: "pending", label: "⏳ Pending" },
  { key: "dmca_filed", label: "📨 DMCA Filed" },
  { key: "resolved", label: "✅ Resolved" },
  { key: "ignored", label: "🙈 Ignored" },
  { key: "all", label: "Všetky" },
];

const ACTIONS = [
  { key: "dmca_filed", label: "📨 DMCA Filed", cls: "bg-amber-600 hover:bg-amber-500 text-black" },
  { key: "resolved", label: "✅ Resolved", cls: "bg-emerald-600 hover:bg-emerald-500 text-black" },
  { key: "ignored", label: "🙈 Ignore", cls: "bg-zinc-700 hover:bg-zinc-600 text-zinc-100" },
];

export default function AdminAntiRankPillTab() {
  const [status, setStatus] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [byDomain, setByDomain] = useState({});
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState("");
  const [actionFor, setActionFor] = useState("");

  const token = () => localStorage.getItem("admin_token") || "";

  const loadStatus = async () => {
    try {
      const r = await axios.get(`${API}/anti-rankpill/status`);
      setStatus(r.data);
    } catch (e) { /* public endpoint, ignore */ }
  };

  const loadIncidents = async () => {
    if (!token()) { setErr("Admin token chýba. Prihlás sa znova."); return; }
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/anti-rankpill/incidents?status=${filter}&limit=200`,
        { headers: { "X-Admin-Token": token() } });
      setIncidents(r.data.incidents || []);
      setByDomain(r.data.by_domain || {});
    } catch (e) {
      setErr(e.response?.data?.detail || "load_failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStatus(); }, []);
  useEffect(() => { loadIncidents(); /* eslint-disable-next-line */ }, [filter]);

  const doAction = async (incident_id, action) => {
    if (!token()) return;
    setActionFor(incident_id);
    try {
      await axios.post(`${API}/anti-rankpill/incidents/action`,
        { incident_id, action },
        { headers: { "X-Admin-Token": token() } });
      await loadIncidents();
      await loadStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || "action_failed");
    } finally { setActionFor(""); }
  };

  const scanNow = async () => {
    if (!token()) return;
    setScanning(true); setErr("");
    try {
      const r = await axios.post(`${API}/anti-rankpill/scan-now`, {},
        { headers: { "X-Admin-Token": token() } });
      alert(`Scan hotový.\nNaskenovaných kanárikov: ${r.data.scanned}\nNové incidenty: ${r.data.new_incidents}`);
      await loadIncidents(); await loadStatus();
    } catch (e) {
      setErr(e.response?.data?.detail || "scan_failed");
    } finally { setScanning(false); }
  };

  return (
    <div className="p-4 space-y-5" data-testid="anti-rankpill-tab">
      <header>
        <h2 className="text-xl font-bold flex items-center gap-2">
          🛡️ Anti-RankPill Defense
          <span className="text-[10px] tracking-widest bg-rose-500 text-black px-2 py-0.5 font-black uppercase">V190</span>
        </h2>
        <p className="text-sm text-zinc-400 mt-1">
          3-layer ochrana proti AI scraperom (RankPill, Surfer auto-content). Meta tagy + canary fingerprints + Google CSE týždenné skenovanie.
        </p>
      </header>

      {/* Status overview */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="anti-rankpill-status">
        <StatCard label="Aktívne kanáriky" value={status?.canaries_active ?? "—"} accent="emerald" />
        <StatCard label="Incidenty (total)" value={status?.incidents_total ?? "—"} accent="zinc" />
        <StatCard label="Pending" value={status?.incidents_pending ?? "—"} accent="rose" />
        <StatCard label="Google CSE"
                  value={status?.google_cse_configured ? "✅ Configured" : "❌ Missing"}
                  accent={status?.google_cse_configured ? "emerald" : "amber"} />
        <StatCard label="Anti-AI Meta"
                  value={status?.anti_ai_meta_blocking ? "✅ Active" : "—"}
                  accent="emerald" />
      </section>

      {/* Controls */}
      <section className="flex items-center gap-2 flex-wrap">
        {STATUS_FILTERS.map(f => (
          <button key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`anti-rankpill-filter-${f.key}`}
                  className={`text-xs px-3 py-1.5 border ${filter === f.key
                    ? "bg-rose-600 border-rose-500 text-white font-black"
                    : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:bg-zinc-800"}`}>
            {f.label}
          </button>
        ))}
        <span className="flex-1" />
        <button onClick={scanNow}
                disabled={scanning}
                data-testid="anti-rankpill-scan-now"
                className="text-xs px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-black font-black uppercase tracking-wider disabled:opacity-50">
          {scanning ? "Skenujem…" : "🔍 Scan Now"}
        </button>
        <button onClick={() => { loadStatus(); loadIncidents(); }}
                className="text-xs px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          🔄 Refresh
        </button>
      </section>

      {err && (
        <div className="bg-rose-950/40 border border-rose-700 text-rose-300 p-3 text-sm" data-testid="anti-rankpill-error">
          {err}
        </div>
      )}

      {/* Top offending domains */}
      {Object.keys(byDomain).length > 0 && (
        <section className="border border-zinc-800 bg-zinc-950 p-3">
          <h3 className="text-xs uppercase tracking-widest text-zinc-500 font-black mb-2">🚨 Top zlodejské domény</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byDomain)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 12)
              .map(([dom, n]) => (
                <span key={dom} className="text-[11px] bg-rose-950/40 border border-rose-700 text-rose-200 px-2 py-1 font-mono">
                  {dom} · {n}
                </span>
              ))}
          </div>
        </section>
      )}

      {/* Incidents table */}
      <section className="border border-zinc-800 bg-zinc-950" data-testid="anti-rankpill-incidents-list">
        {loading && <div className="p-4 text-zinc-500 text-sm">Načítavam incidenty…</div>}
        {!loading && incidents.length === 0 && (
          <div className="p-6 text-center text-zinc-500 text-sm">
            🎉 Žiadne incidenty v tomto stave. {filter === "pending" && "Tvoj obsah je čistý."}
          </div>
        )}
        {!loading && incidents.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-zinc-900 text-zinc-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-2">Detected</th>
                <th className="text-left p-2">Domain</th>
                <th className="text-left p-2">Stolen URL</th>
                <th className="text-left p-2">Owner</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Akcie</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map(inc => (
                <tr key={inc.incident_id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                    data-testid={`anti-rankpill-incident-${inc.incident_id}`}>
                  <td className="p-2 text-zinc-500 whitespace-nowrap font-mono">
                    {(inc.detected_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="p-2 font-bold text-rose-300">{inc.domain}</td>
                  <td className="p-2 max-w-[320px] truncate">
                    <a href={inc.url} target="_blank" rel="noopener noreferrer"
                       className="text-amber-300 hover:underline" title={inc.url}>
                      {inc.title || inc.url}
                    </a>
                    {inc.snippet && (
                      <div className="text-zinc-500 truncate text-[10px]">{inc.snippet}</div>
                    )}
                  </td>
                  <td className="p-2 text-zinc-400 font-mono">{inc.owner_email}</td>
                  <td className="p-2">
                    <span className={`text-[10px] px-2 py-0.5 font-black uppercase tracking-wider ${
                      inc.status === "pending" ? "bg-rose-700 text-white" :
                      inc.status === "dmca_filed" ? "bg-amber-700 text-white" :
                      inc.status === "resolved" ? "bg-emerald-700 text-white" :
                      "bg-zinc-700 text-zinc-300"
                    }`}>{inc.status}</span>
                  </td>
                  <td className="p-2 space-x-1 whitespace-nowrap">
                    {inc.status === "pending" && ACTIONS.map(a => (
                      <button key={a.key}
                              disabled={actionFor === inc.incident_id}
                              onClick={() => doAction(inc.incident_id, a.key)}
                              data-testid={`anti-rankpill-action-${a.key}-${inc.incident_id}`}
                              className={`text-[10px] px-2 py-1 font-black uppercase tracking-wider disabled:opacity-40 ${a.cls}`}>
                        {a.label}
                      </button>
                    ))}
                    {inc.status !== "pending" && (
                      <span className="text-[10px] text-zinc-500">— akcie sú zamknuté —</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Footer help */}
      <section className="border border-zinc-800 bg-zinc-950 p-4 text-xs text-zinc-400 leading-relaxed">
        <h3 className="text-zinc-500 uppercase tracking-widest font-black mb-2">ℹ️ Ako to funguje</h3>
        <ul className="space-y-1 list-disc list-inside">
          <li><strong className="text-zinc-200">Layer 1 — Passive:</strong> Každý článok obsahuje <code className="bg-zinc-800 px-1">noai, noimageai</code> meta tagy (blokuje GPTBot, ClaudeBot, PerplexityBot, ...)</li>
          <li><strong className="text-zinc-200">Layer 2 — Detective:</strong> Každý článok má unikátny <code className="bg-zinc-800 px-1">trp-canary-XXXX</code> fingerprint v skrytom HTML komentári + aria-hidden span.</li>
          <li><strong className="text-zinc-200">Layer 3 — Reactive:</strong> Pondelňajší cron prehľadá Google CSE pre každý canary token. Ak ho nájde mimo zákazníkovho domain, vytvorí incident.</li>
          <li>Bez <code className="bg-zinc-800 px-1">GOOGLE_CSE_API_KEY</code> a <code className="bg-zinc-800 px-1">GOOGLE_CSE_CX</code> v <code className="bg-zinc-800 px-1">.env</code> beží len Layer 1 + 2. Layer 3 (skenovanie) je no-op.</li>
        </ul>
      </section>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const colors = {
    emerald: "border-emerald-700 bg-emerald-950/30 text-emerald-300",
    rose: "border-rose-700 bg-rose-950/30 text-rose-300",
    amber: "border-amber-700 bg-amber-950/30 text-amber-300",
    zinc: "border-zinc-700 bg-zinc-950 text-zinc-300",
  };
  return (
    <div className={`border ${colors[accent] || colors.zinc} p-3`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 font-black">{label}</div>
      <div className="text-xl font-black mt-1">{value}</div>
    </div>
  );
}
